/*
 * File: Button.styles.js
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on February 7, 2022 at 1:53 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: September 25, 2024 at 2:04 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled, { css } from 'styled-components';

// Styles
import { ColorValues } from '../../styles/colors';


/**
 * Constants
 */

const SIZES = {
	small: css`
		padding: 0.3125rem 0.625rem;
	`,
	medium: css`
		padding: 0.5625rem 1.5625rem;
	`,
	large: css`
		padding: 0.8625rem 1.8625rem;
	`,
};

const VARIATIONS = {
	default: {
		solid: css`
			background-color: ${({ theme }) => theme.primaryButton};
			border-color: ${({ theme }) => theme.primaryButton};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.primaryButton};
			color: ${({ theme }) => theme.primaryButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.primaryButton};
		`,
	},
	light: {
		solid: css`
			background-color: ${({ theme }) => theme.lightButton};
			border-color: ${({ theme }) => theme.lightButton};
			color: ${({ theme }) => theme.primaryButton};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.primaryButton};
			color: ${({ theme }) => theme.primaryButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.primaryButton};
		`,
	},
	white: {
		solid: css`
			background-color: ${ColorValues.white};
			border-color: ${ColorValues.white};
			color: ${ColorValues.dark};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${ColorValues.white};
			color: ${ColorValues.white};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${ColorValues.white};
		`,
	},
	dark: {
		solid: css`
			background-color: ${({ theme }) => theme.darkButton};
			border-color: ${({ theme }) => theme.darkButton};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.darkButton};
			color: ${({ theme }) => theme.darkButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.darkButton};
		`,
	},
	secondary: {
		solid: css`
			background-color: ${({ theme }) => theme.secondaryButton};
			border-color: ${({ theme }) => theme.secondaryButton};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.secondaryButton};
			color: ${({ theme }) => theme.secondaryButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.secondaryButton};
		`,
	},
	tertiary: {
		solid: css`
			background-color: ${({ theme }) => theme.brandTertiaryBase};
			border-color: ${({ theme }) => theme.brandTertiaryBase};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.brandTertiaryBase};
			color: ${({ theme }) => theme.brandTertiaryBase};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.brandTertiaryBase};
		`,
	},
	warning: {
		solid: css`
			background-color: ${({ theme }) => theme.statusDangerBase};
			border-color: ${({ theme }) => theme.statusDangerBase};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.statusDangerBase};
			color: ${({ theme }) => theme.statusDangerBase};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.statusDangerBase};
		`,
	},
	button2: {
		solid: css`
			background-color: ${({ theme }) => theme.brandSecondaryBase};
			border-color: ${({ theme }) => theme.brandSecondaryBase};
			color: ${({ theme }) => theme.dark};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.dark};
			color: ${({ theme }) => theme.primaryButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.primaryButton};
		`,
	},
};


/**
 * Styles
 */

export const Button = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	appearance: none;
	border-radius: 50px;
	letter-spacing: normal;
	cursor: pointer;
	border-width: 1px;
	border-style: solid;
	font-weight: 600;
	font-size: 1.0rem;
	position: relative;

	* {
		letter-spacing: normal;
	}

	& > * + * {
		margin-left: 0.625rem;
	}
	.noMargin {
		margin-left: 0px;
	}

	${({ size, variant, variation }) => css`
		${SIZES[size]}
		${VARIATIONS[variation][variant]}
	`}

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		opacity: 0.8;
		cursor: default;
	}
`;

export const SpinnerContainer = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
`;

export const Span = styled.span`
	margin: 0px !important;
	height: auto !important;
`;
